<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-large"
  >
    <template v-slot:header>
      <div class="d-flex justify-space-between w-100">
        <v-card-title class="screen-title"
          >イントラネットFW プラットフォームゲートウェイ
          ゾーン選択</v-card-title
        >
        <button
          type="button"
          aria-label="Close"
          class="close"
          @click="closeModal"
        >
          ×
        </button>
      </div>
    </template>
    <template v-if="isLoaded" v-slot:body>
      <v-card-text class="pa-0">
        <div class="modal-body">
          <div class="d-flex flex-wrap ga-3 justify-end my-4">
            <v-btn color="primary" @click="downloadConfig" v-permission.config
              >設定ダウンロード
            </v-btn>
            <v-btn color="primary" @click="uploadConfig" v-permission.config
              >設定アップロード
            </v-btn>
          </div>
          <app-table
            primary-key="zoneRelationId"
            :hover="true"
            :fields="zoneSelect.fields"
            :items="items"
            :per-page="-1"
            @click:row="showPolicyModal"
          >
            <template #cell(arrow)> ⇒ </template>
          </app-table>
        </div>
        <v-alert class="my-4" v-if="!!uploadAlert">{{ uploadAlert }}</v-alert>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn
              variant="flat"
              color="primary"
              @click="putPolicy"
              v-permission.config
            >
              ポリシー設定反映
            </v-btn>
            <v-btn variant="outlined" color="primary" @click="closeModal"
              >キャンセル
            </v-btn>
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type {
  IntranetFwPolicy,
  IntraPolicy,
  PostZonePolicyInfo,
  ZonePolicyInfo,
} from "@/apis/IntranetfwPolicyApi";
import { UploadError } from "@/services/ConfigDownloadService";
import type { Pfgw } from "@/apis/IntranetfwServiceMenuApi";
import type { PfgwZone } from "@/apis/PfgwZoneApi";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import IntranetFwPfgwPolicyList from "@/modals/applianceContractSetting/IntranetFirewall/policy/pfgw/IntranetFwPfgwPolicyList.vue";
import IntranetFwPfgwPolicyConfirm from "@/modals/applianceContractSetting/IntranetFirewall/policy/pfgw/IntranetFwPfgwPolicyConfirm.vue";
import { VueFinalModal } from "vue-final-modal";

/** PFGWゾーン */
export type PfgwZoneType = "WVS2" | "KCPS";

export default defineComponent({
  name: "IntranetFwPfgwPolicyZoneList",
  props: {
    /** PFGW */
    pfgw: {
      type: Object as PropType<Pfgw>,
      required: true,
    },
    /** VPNコード */
    vpnCode: {
      type: String as PropType<string>,
      required: true,
    },
  },
  data() {
    return {
      pfgwZone: {} as PfgwZone,
      intranetFwPolicy: {} as IntranetFwPolicy,
      /** モーダル入室時のスナップショット */
      originalPolicy: {} as IntranetFwPolicy,
      zoneSelect: {
        fields: [
          {
            key: "srcZone",
            title: "送信元ゾーン",
            width: "130px",
          },
          { key: "arrow", title: "", width: "120px" },
          {
            key: "dstZone",
            title: "宛先ゾーン",
            width: "130px",
          },
          { key: "space", title: "", width: "200px" },
        ],
      },
      /** 初期通信完了フラグ */
      isLoaded: false,
      uploadAlert: null as string | null,
    };
  },
  computed: {
    /** WVS2 → KCPSのポリシー情報. ゾーンIDが一致するものがない場合はundefined */
    wvs2ToKcpsPolicyInfo():
      | (ZonePolicyInfo & { enumberAct: string })
      | undefined {
      if (
        this.pfgwZone.zoneIdList.find(
          (e) => e.srcZoneId === this.pfgwZone.wvs2ZoneId
        )
      ) {
        const policyInfo = this.intranetFwPolicy.wvs2ToKcpsList.find(
          (e) => e.enumberAct === this.pfgw.enumberAct
        );
        if (policyInfo) {
          return policyInfo;
        } else {
          // ゾーン接続はあるが一度も設定していない場合はGETで取得できない
          // ポリシー0件でデータを作成
          return {
            enumberAct: this.pfgw.enumberAct,
            zoneRelationId: this.pfgw.wvs2ToKcpsZoneRelationId!,
            total: 0,
            policyList: [],
          };
        }
      } else {
        // ゾーン接続が存在しない
        return undefined;
      }
    },
    /** KCPS → WVS2のポリシー情報. ゾーンIDが一致するものがない場合はundefined */
    kcpsToWvs2PolicyInfo():
      | (ZonePolicyInfo & { enumberAct: string })
      | undefined {
      if (
        this.pfgwZone.zoneIdList.find(
          (e) => e.srcZoneId === this.pfgwZone.kcpsZoneId
        )
      ) {
        const policyInfo = this.intranetFwPolicy.kcpsToWvs2List.find(
          (e) => e.enumberAct === this.pfgw.enumberAct
        );
        if (policyInfo) {
          return policyInfo;
        } else {
          // ゾーン接続はあるが一度も設定していない場合はGETで取得できない
          // ポリシー0件でデータを作成
          return {
            enumberAct: this.pfgw.enumberAct,
            zoneRelationId: this.pfgw.kcpsToWvs2ZoneRelationId!,
            total: 0,
            policyList: [],
          };
        }
      } else {
        // ゾーン接続が存在しない
        return undefined;
      }
    },
    /** WVS2 ←→ KCPSの一覧 */
    items(): {
      zoneRelationId: string;
      srcZone: PfgwZoneType;
      dstZone: PfgwZoneType;
    }[] {
      let items: {
        zoneRelationId: string;
        srcZone: PfgwZoneType;
        dstZone: PfgwZoneType;
      }[] = [];
      if (this.wvs2ToKcpsPolicyInfo) {
        items.push({
          zoneRelationId: this.wvs2ToKcpsPolicyInfo.zoneRelationId,
          srcZone: "WVS2",
          dstZone: "KCPS",
        });
      }
      if (this.kcpsToWvs2PolicyInfo) {
        items.push({
          zoneRelationId: this.kcpsToWvs2PolicyInfo.zoneRelationId,
          srcZone: "KCPS",
          dstZone: "WVS2",
        });
      }
      return items;
    },
    /** 変更があるかどうかを判定（policyIntranetFwKeyIdは比較から除外） */
    hasChanges(): boolean {
      const normalize = (p: IntranetFwPolicy) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { policyIntranetFwKeyId, ...rest } =
          p ?? ({} as IntranetFwPolicy);
        return rest;
      };
      return !isEqual(
        normalize(this.intranetFwPolicy),
        normalize(this.originalPolicy)
      );
    },
  },
  async mounted() {
    this.pfgwZone = await this.$api.pfgwZoneRelation.getPfgwZone(
      this.pfgw.enumberAct
    );
    this.intranetFwPolicy =
      await this.$api.intranetFwPolicy.getPolicyIntranetFw("INTRANET_FW_PFGW");
    this.originalPolicy = cloneDeep(this.intranetFwPolicy);
    this.isLoaded = true;
  },
  methods: {
    /** ×ボタン／キャンセル押下時の共通クローズ処理 */
    async closeModal(): Promise<void> {
      if (!this.hasChanges) {
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
        return;
      }
      try {
        await this.$confirm("変更を破棄してキャンセルします、よろしいですか？");
        (
          this.$refs.modal as typeof VueFinalModal & { cancel: () => void }
        ).cancel();
      } catch {
        // ダイアログでキャンセル → 何もしない
        return;
      }
    },
    /**
     * ポリシー画面へ遷移
     * @param srcZone 送信元ゾーン
     */
    async showPolicyModal({ srcZone }: { srcZone: PfgwZoneType }) {
      this.uploadAlert = null;

      const cfg: {
        list: (ZonePolicyInfo & { enumberAct: string })[];
        zoneRelationId: string;
      } =
        srcZone === "WVS2"
          ? {
              list: this.intranetFwPolicy.wvs2ToKcpsList,
              zoneRelationId: this.pfgw.wvs2ToKcpsZoneRelationId!,
            }
          : {
              list: this.intranetFwPolicy.kcpsToWvs2List,
              zoneRelationId: this.pfgw.kcpsToWvs2ZoneRelationId!,
            };

      // 既存レコードを検索（なければ undefined）
      const existing = cfg.list.find(
        (e) => e.enumberAct === this.pfgw.enumberAct
      );

      try {
        // 子にスナップショットを渡す
        const edited: IntraPolicy[] = await this.$modal.show(
          IntranetFwPfgwPolicyList,
          {
            policyList: cloneDeep(existing?.policyList ?? []),
            intranetFwPolicy: this.intranetFwPolicy,
            srcZone,
            pfgw: this.pfgw,
          }
        );

        // 変更なしなら何もしない
        if (existing) {
          if (!isEqual(edited, existing.policyList)) {
            existing.policyList = edited;
            existing.total = edited.length;
          }
          return;
        }

        // 初回作成：編集結果があれば新規追加（空なら何もしない）
        if (edited.length > 0) {
          cfg.list.push({
            enumberAct: this.pfgw.enumberAct,
            zoneRelationId: cfg.zoneRelationId,
            total: edited.length,
            policyList: edited,
          });
        }
      } catch {
        // ダイアログでキャンセル → 何もしない
      }
    },
    /** ポリシー設定反映 */
    async putPolicy() {
      this.uploadAlert = null;
      // 設定反映確認モーダルを表示
      await this.$modal.show(IntranetFwPfgwPolicyConfirm, {
        pfgwPolicy: {
          wvs2ToKcps: this.wvs2ToKcpsPolicyInfo,
          kcpsToWvs2: this.kcpsToWvs2PolicyInfo,
        },
      });
      // ポリシー更新APIの呼び出し
      await this.$api.intranetFwPolicy.postPolicyIntranetFw(
        "INTRANET_FW_PFGW",
        this.pfgw.enumberAct,
        {
          privateToPrivateList: [],
          extraToPrivateList: [],
          privateToExtraList: [],
          wvs2ToKcps: createReqPolicyInfo(this.wvs2ToKcpsPolicyInfo),
          kcpsToWvs2: createReqPolicyInfo(this.kcpsToWvs2PolicyInfo),
          note: null,
          policyIntranetFwKeyId: this.intranetFwPolicy.policyIntranetFwKeyId,
        }
      );
      // モーダルを閉じる
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** 設定ダウンロード */
    downloadConfig() {
      // GETポリシーに編集中のポリシーをマージ
      const downloadPolicy = cloneDeep(this.intranetFwPolicy);
      if (this.wvs2ToKcpsPolicyInfo) {
        const info = downloadPolicy.wvs2ToKcpsList.find(
          (e) => e.enumberAct === this.wvs2ToKcpsPolicyInfo!.enumberAct
        );
        if (info) {
          info.policyList = this.wvs2ToKcpsPolicyInfo.policyList;
        } else {
          // 初回時はGETにポリシーが存在しないため追加
          downloadPolicy.wvs2ToKcpsList = [
            ...downloadPolicy.wvs2ToKcpsList,
            this.wvs2ToKcpsPolicyInfo,
          ];
        }
      }
      if (this.kcpsToWvs2PolicyInfo) {
        const info = downloadPolicy.kcpsToWvs2List.find(
          (e) => e.enumberAct === this.kcpsToWvs2PolicyInfo!.enumberAct
        );
        if (info) {
          info.policyList = this.kcpsToWvs2PolicyInfo.policyList;
        } else {
          // 初回時はGETにポリシーが存在しないため追加
          downloadPolicy.kcpsToWvs2List = [
            ...downloadPolicy.kcpsToWvs2List,
            this.kcpsToWvs2PolicyInfo,
          ];
        }
      }

      this.$service.configDownload.downloadIntranetFwPfgwPolicy(
        this.vpnCode,
        this.pfgw.enumberAct,
        downloadPolicy
      );
    },
    /** 設定アップロード */
    async uploadConfig() {
      try {
        const uploadPolicy =
          await this.$service.configDownload.uploadIntranetFwPfgwPolicy(
            this.vpnCode,
            this.pfgw.enumberAct,
            this.intranetFwPolicy
          );
        this.uploadAlert = null;
        await this.$confirm(
          "ポリシー設定を上書きします。設定反映していない設定は破棄されます。よろしいですか？"
        );
        this.intranetFwPolicy = uploadPolicy;
      } catch (e) {
        this.uploadAlert = (e as UploadError).message;
      }
    },
  },
});

/** ポリシー更新APIの設定データ作成 */
function createReqPolicyInfo(
  info: ZonePolicyInfo | undefined
): PostZonePolicyInfo | null {
  return info
    ? {
        zoneRelationId: info.zoneRelationId,
        policyList: info.policyList
          .filter((e) => e.policyCategoryInfo !== "UNEDITABLE_DEFAULT_POLICY")
          .map((e) => {
            return {
              ...e,
              description: e.description || null,
              srcAddressList: e.srcAddressList.map((v) => v.ipAddressId),
              dstAddressList: e.dstAddressList.map((v) => v.ipAddressId),
              serviceList: e.serviceList.map((v) => v.serviceSeq),
              customServiceList: e.customServiceList.map(
                (v) => v.customServiceSeq
              ),
            };
          }),
      }
    : null;
}
</script>
